import { useMemo } from 'react'
import type { HydraulicSystem } from '@shared/hydraulic'
import { resolveScenario, DEFAULT_BASE_SCENARIO_ID } from '@shared/hydraulic'
import { useModelStore } from './model-store'

/**
 * The "resolved" view of the canonical system for the active scenario.
 * Base-active (or a legacy system with no scenario tree) returns the SAME
 * reference (resolveScenario short-circuits), so this is a no-op until a
 * non-Base scenario is active — keeping Zustand selectors stable.
 *
 * A module-level single-entry memo keyed on (system ref, active_id) ensures all
 * readers calling this get the SAME resolved reference under a non-Base scenario
 * (without it, each reader's own useMemo would structuredClone independently and
 * produce divergent references). Single-entry is sufficient: every reader resolves
 * the same (system, active_id) within a render. The store creates a NEW system
 * reference on every mutation, so the ref key is sound.
 */
let _memo: { system: HydraulicSystem; activeId: string; resolved: HydraulicSystem } | null = null

export function deriveResolved(system: HydraulicSystem | null): HydraulicSystem | null {
  if (!system) return null
  if (!system.scenarios) return system
  const activeId = system.scenarios.active_id ?? DEFAULT_BASE_SCENARIO_ID
  if (_memo && _memo.system === system && _memo.activeId === activeId) return _memo.resolved
  const resolved = resolveScenario(system, activeId) // Base-active returns `system` itself
  _memo = { system, activeId, resolved }
  return resolved
}

/**
 * React hook: the resolved system for the active scenario. Recomputes only when
 * the canonical `system` reference or the active scenario id changes.
 */
export function useResolvedSystem(): HydraulicSystem | null {
  const system = useModelStore((s) => s.system)
  const activeId = useModelStore((s) => s.system?.scenarios?.active_id)
  // ⚠️ `activeId` is load-bearing even though the callback never names it:
  // deriveResolved reads system.scenarios.active_id itself, and the store can
  // switch the active scenario without replacing the `system` reference (which
  // is precisely why deriveResolved's own module-level cache is keyed on BOTH).
  // Drop it, as exhaustive-deps suggests, and switching scenarios returns the
  // previously resolved system.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => deriveResolved(system), [system, activeId])
}
